import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';

import { sortSpecials, isCumulative } from 'shared/order/utilities';
import { productSatisfiesSaleWeight } from 'shared/helpers/products';

export const calculateSaleDiscounts = ({
  applyStowedDiscounts,
  compositePrice,
  credit,
  discountTaxOrder,
  detail,
  originalCompositePrice,
  medicalOrder,
  productTotal,
  receiptDetail,
  specialData,
  globalSpecialsSettings,
  stowDiscounts,
  stowedDiscounts,
}) => {
  let saleSpecials;
  const brandSale = detail.productBrandId ?? detail.productBrand;
  const salesType = [brandSale, detail.productCategory, detail.productSubcategory, detail.id];

  // if we don't have any special restrictions, don't worry about weights, they all apply then
  const hasApplicableSpecial = specialData?.saleSpecials
    ? productSatisfiesSaleWeight(salesType, specialData.saleSpecials, detail.option)
    : true;

  if (hasApplicableSpecial) {
    if (applyStowedDiscounts) {
      saleSpecials = _.reduce(
        stowedDiscounts,
        (saleDiscounts, discount) => {
          if (discount.type === 'sale') {
            saleDiscounts.push({ ...discount.discountData, _quantityToApply: discount.quantityToApply || 0 });
          }
          return saleDiscounts;
        },
        []
      );
    } else {
      saleSpecials = specialData?.saleSpecials || [];

      // Legacy special data support
      if (saleSpecials.length < 1 && (!_.isNil(specialData?.discount) || !_.isNil(specialData?.productDiscounts))) {
        const inProductDiscounts = !_.isNil(specialData?.productDiscounts?.[detail.id]);
        saleSpecials.push({
          discount: inProductDiscounts ? specialData.productDiscounts[detail.id] : specialData?.discount,
          menuType: `both`,
          percentDiscount: !_.isNil(specialData?.percentDiscount) ? specialData.percentDiscount : inProductDiscounts,
        });
      }
    }
  }

  saleSpecials = sortSpecials(saleSpecials, {
    // Partial product object here from the detail data
    brandId: detail.productBrandId,
    brandName: detail.productBrand,
    _id: detail.id,
    subcategory: detail.productSubcategory,
    type: detail.productCategory,
  });

  _.forEach(saleSpecials, (saleSpecial) => {
    const stackingBehaviorIsCumulative = isCumulative(
      saleSpecial.stackingBehavior,
      globalSpecialsSettings.stackingBehavior
    );
    const startingPrice = stackingBehaviorIsCumulative ? originalCompositePrice : compositePrice;
    const individualPrice = startingPrice.div(detail.quantity);

    const specialMenuType = saleSpecial.menuType || `both`;
    // should we apply this special to the product?
    const specialAppliesToWeight = saleSpecial && productSatisfiesSaleWeight(salesType, [saleSpecial], detail.option);
    if (
      specialAppliesToWeight &&
      (specialMenuType === `both` ||
        (specialMenuType === `medical` && medicalOrder) ||
        (specialMenuType === `recreational` && !medicalOrder))
    ) {
      const quantityToApply = !_.isNil(saleSpecial?._quantityToApply)
        ? saleSpecial._quantityToApply
        : detail.quantity || 0;
      let additionalCredit = Big(0);
      let individualDiscount;

      if (saleSpecial?.percentDiscount) {
        individualDiscount = individualPrice.times(Big(saleSpecial?.discount || 0).div(100));
        additionalCredit = additionalCredit.add(individualDiscount.times(quantityToApply));
      } else if (saleSpecial?.targetPrice) {
        individualDiscount = individualPrice.minus(Big(saleSpecial?.discount || 0));
        additionalCredit = additionalCredit.add(individualDiscount.times(quantityToApply));
      } else {
        individualDiscount = Big(saleSpecial?.discount || 0);
        additionalCredit = additionalCredit.add(individualDiscount.times(quantityToApply));
      }

      if (stowDiscounts) {
        stowedDiscounts.push({
          applicableQuantity: detail.quantity,
          discountData: saleSpecial,
          individualDiscount,
          key: saleSpecial.specialId,
          type: `sale`,
        });
      } else {
        compositePrice = compositePrice.minus(additionalCredit.round(2, 1));

        // Depending on tax config, add the credit into the total. This is to make the subtotal on the checkout ticket
        // look more like the menu.
        if (discountTaxOrder === 'taxesFirst' || Big(detail.basePriceMult, `Base Price Mult`).eq(1)) {
          credit = credit.plus(additionalCredit.round(2, 1));
        } else {
          productTotal = productTotal.minus(additionalCredit.round(2, 1));
        }

        if (receiptDetail && additionalCredit.gt(0)) {
          receiptDetail.addDiscount({
            type: `sale`,
            value: additionalCredit.times(100),
            id: saleSpecial.specialId,
            name: saleSpecial.specialName,
          });
        }
      }
    }
  });

  return { compositePrice, credit, productTotal, stowedDiscounts };
};
